import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface RentalListing {
  id: string;
  title: string;
  description: string;
  category: string;
  subcategory: string;
  condition: string;
  price: number;
  price_unit: string;
  min_rental_period: string;
  max_rental_period: string;
  deposit_required: boolean;
  deposit_amount: number;
  delivery_available: boolean;
  pickup_required: boolean;
  insurance_required: boolean;
  images: string[];
  location: string;
  latitude: string;
  longitude: string;
  seller_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  listing_type: string;
  available_from_date: string;
}

interface RentalOffer {
  id: string;
  rental_listing_id: string;
  renter_id: string;
  rental_owner_id: string;
  rental_price: number;
  deposit_amount: number;
  total_amount: number;
  start_date: string;
  end_date: string;
  rental_period: string;
  price_unit: string;
  note?: string;
  status: string;
  payment_status: string;
  payment_intent_id?: string;
  client_secret?: string;
  payment_authorized_at?: string;
  payment_captured_at?: string;
  created_at: string;
  updated_at: string;
  listing_title?: string;
  owner_name?: string;
}

const Rentals: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'listings' | 'orders'>('listings');
  const [listings, setListings] = useState<RentalListing[]>([]);
  const [offers, setOffers] = useState<RentalOffer[]>([]);
  const [userOffers, setUserOffers] = useState<RentalOffer[]>([]); // Offers made BY the user
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  const [expandedListings, setExpandedListings] = useState<Set<string>>(new Set());
  
  // Search states for each tab
  const [listingsSearchTerm, setListingsSearchTerm] = useState('');
  const [ordersSearchTerm, setOrdersSearchTerm] = useState('');
  const [filteredListings, setFilteredListings] = useState<RentalListing[]>([]);
  const [filteredUserOffers, setFilteredUserOffers] = useState<RentalOffer[]>([]);

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Filter listings based on search term
  useEffect(() => {
    if (listingsSearchTerm.trim() === '') {
      setFilteredListings(listings);
    } else {
      const filtered = listings.filter(listing => 
        (listing.title && typeof listing.title === 'string' && listing.title.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.category && typeof listing.category === 'string' && listing.category.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.subcategory && typeof listing.subcategory === 'string' && listing.subcategory.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.location && typeof listing.location === 'string' && listing.location.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.status && typeof listing.status === 'string' && listing.status.toLowerCase().includes(listingsSearchTerm.toLowerCase())) ||
        (listing.listing_type && typeof listing.listing_type === 'string' && listing.listing_type.toLowerCase().includes(listingsSearchTerm.toLowerCase()))
      );
      setFilteredListings(filtered);
    }
  }, [listingsSearchTerm, listings]);

  // Filter user offers based on search term
  useEffect(() => {
    if (ordersSearchTerm.trim() === '') {
      setFilteredUserOffers(userOffers);
    } else {
      const filtered = userOffers.filter(offer => 
        (offer.listing_title && typeof offer.listing_title === 'string' && offer.listing_title.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.status && typeof offer.status === 'string' && offer.status.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.payment_status && typeof offer.payment_status === 'string' && offer.payment_status.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.rental_owner_id && typeof offer.rental_owner_id === 'string' && offer.rental_owner_id.toLowerCase().includes(ordersSearchTerm.toLowerCase())) ||
        (offer.renter_id && typeof offer.renter_id === 'string' && offer.renter_id.toLowerCase().includes(ordersSearchTerm.toLowerCase()))
      );
      setFilteredUserOffers(filtered);
    }
  }, [ordersSearchTerm, userOffers]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user email first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('No active session');
        return;
      }

      // Fetch user profile to get email and name
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('email, first_name, last_name, business_name, profile_type')
        .eq('id', userId)
        .single();

      if (profile?.email) {
        setUserEmail(profile.email);
      }

      // Set display name (business name for business profiles, first + last name for individuals)
      if (profile?.profile_type === 'business' && profile?.business_name) {
        setUserDisplayName(profile.business_name);
      } else if (profile?.first_name || profile?.last_name) {
        const firstName = profile?.first_name || '';
        const lastName = profile?.last_name || '';
        setUserDisplayName(`${firstName} ${lastName}`.trim());
      } else {
        setUserDisplayName(`User ID: ${userId}`);
      }

      // Fetch user's rental listings
      const { data: listingsData, error: listingsError } = await supabase
        .from('rental_listings')
        .select('*')
        .eq('seller_id', userId)
        .order('created_at', { ascending: false });

      if (listingsError) {
        // Error fetching rental listings
      } else {
        setListings(listingsData || []);
      }

      // Fetch rental offers where user is renter or owner
      

      
      // Get offers where user is the renter (has made offers for rentals)
      const { data: userOffersData } = await supabase
        .from('rental_offers')
        .select('*')
        .eq('renter_id', userId)
        .order('created_at', { ascending: false });


      
      // Get ALL offers where the user is the owner (regardless of current listings)
      const { data: ownerOffersData } = await supabase
        .from('rental_offers')
        .select('*')
        .eq('rental_owner_id', userId)
        .order('created_at', { ascending: false });
      

      
      // Set offers for User Listings tab (offers made TO user's listings)
      if (ownerOffersData && ownerOffersData.length > 0) {
        setOffers(ownerOffersData);
      } else {
        setOffers([]);
      }
      
      // Set user offers for User Rental Orders tab (offers made BY the user)
      if (userOffersData && userOffersData.length > 0) {
        setUserOffers(userOffersData);
      } else {
        setUserOffers([]);
      }

    } catch (err) {
      setError('Error fetching user rental data');
    } finally {
      setLoading(false);
    }
  };

  const toggleListingExpansion = (listingId: string) => {
    setExpandedListings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listingId)) {
        newSet.delete(listingId);
      } else {
        newSet.add(listingId);
      }
      return newSet;
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'rejected':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBackToDashboard = () => {
    // Get dashboard state from location state or localStorage
    const dashboardState = location.state?.dashboardState || JSON.parse(localStorage.getItem('dashboardState') || '{}');
    
    if (dashboardState.activeSection) {
      // Navigate back to dashboard with preserved state
      navigate('/dashboard', { 
        state: { 
          preserveState: true,
          activeSection: dashboardState.activeSection,
          homeSearchTerm: dashboardState.homeSearchTerm,
          filteredUsers: dashboardState.filteredUsers,
          userManagementSearchTerm: dashboardState.userManagementSearchTerm,
          currentPage: dashboardState.currentPage
        }
      });
    } else {
      // Fallback to simple back navigation
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user rentals...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#00a67e] shadow-sm border-b border-[#008f6b]">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">User Rentals</h1>
              <p className="text-green-100 mt-1">
                {userDisplayName}
              </p>
              <p className="text-green-200 text-sm mt-1">
                {userEmail || `User ID: ${userId}`}
              </p>
            </div>
            <button
              onClick={handleBackToDashboard}
              className="px-4 py-2 bg-[#008f6b] text-white rounded-lg hover:bg-[#007a5a] transition-colors duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('listings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'listings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Listings ({filteredListings.length}/{listings.length})
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'orders'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Rental Orders ({filteredUserOffers.length}/{userOffers.length})
            </button>
          </nav>
        </div>



        {/* Tab Content */}
        {activeTab === 'listings' ? (
          /* User Listings Tab */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Rental Listings</h2>
              <p className="text-sm text-gray-600 mt-1">
                Rental items listed by this user
              </p>
              
              {/* Search Bar */}
              <div className="mt-4">
                <div className="relative max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search rental listings by title, category, location, status, or type..."
                    value={listingsSearchTerm}
                    onChange={(e) => setListingsSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
            
            {listings.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No rental listings found</h3>
                <p className="mt-1 text-sm text-gray-500">This user hasn't listed any rental items yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rental Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rental Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredListings.map((listing) => (
                      <React.Fragment key={listing.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                                <div className="text-sm text-gray-500">{listing.location || 'No location'}</div>
                                <div className="text-xs text-gray-400">ID: {listing.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>
                              <div className="font-medium">{formatPrice(listing.price)}</div>
                              <div className="text-xs text-gray-500">per {listing.price_unit}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{listing.category || 'Uncategorized'}</div>
                              {listing.subcategory && (
                                <div className="text-xs text-gray-400">{listing.subcategory}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>
                              <div>{listing.min_rental_period} - {listing.max_rental_period}</div>
                              <div className="text-xs text-gray-400">{listing.price_unit}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(listing.status)}`}>
                              {listing.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(listing.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleListingExpansion(listing.id)}
                              className="flex items-center justify-center cursor-pointer p-2 bg-[#e6f7f0] rounded-lg hover:bg-[#ccf0e0] transition-colors duration-200"
                            >
                              <svg 
                                className={`w-4 h-4 text-[#00a67e] transition-transform duration-200 ${expandedListings.has(listing.id) ? 'rotate-180' : ''}`} 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                        
                        {/* Listing Details Row (expanded) */}
                        {expandedListings.has(listing.id) && (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 bg-[#e6f7f0]">
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-[#00a67e] mb-3">Rental Details</h4>
                                
                                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                                  <h5 className="text-xs font-medium text-indigo-600 uppercase tracking-wider mb-2">Rental Information</h5>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                    {/* Basic Details */}
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Basic Details</h6>
                                      <div className="space-y-1">
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Condition:</span>
                                          <span className="font-medium capitalize">{listing.condition}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Available From:</span>
                                          <span className="font-medium">{listing.available_from_date ? formatDate(listing.available_from_date) : 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Listing Type:</span>
                                          <span className="font-medium capitalize">{listing.listing_type}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Pricing & Deposit */}
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Pricing & Deposit</h6>
                                      <div className="space-y-1">
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Price:</span>
                                          <span className="font-medium">{formatPrice(listing.price)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Price Unit:</span>
                                          <span className="font-medium">{listing.price_unit}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Deposit Required:</span>
                                          <span className="font-medium">{listing.deposit_required ? 'Yes' : 'No'}</span>
                                        </div>
                                        {listing.deposit_required && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Deposit Amount:</span>
                                            <span className="font-medium">{formatPrice(listing.deposit_amount)}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Delivery & Pickup */}
                                    <div className="bg-gray-50 rounded-lg p-3">
                                      <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Delivery & Pickup</h6>
                                      <div className="space-y-1">
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Delivery Available:</span>
                                          <span className="font-medium">{listing.delivery_available ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Pickup Required:</span>
                                          <span className="font-medium">{listing.pickup_required ? 'Yes' : 'No'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Insurance Required:</span>
                                          <span className="font-medium">{listing.insurance_required ? 'Yes' : 'No'}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* All Offers Section */}
                                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <h5 className="text-xs font-medium text-blue-700 uppercase tracking-wider mb-3">All Offers for This Listing</h5>
                                    
                                    {(() => {
                                      // Find all offers for this listing
                                      const listingOffers = offers.filter(offer => 
                                        offer.rental_listing_id === listing.id
                                      );
                                      

                                      
                                      if (listingOffers.length > 0) {
                                        return listingOffers.map((offer, index) => (
                                          <div key={offer.id} className={`bg-white rounded-lg p-3 border mb-3 ${
                                            offer.status === 'accepted' ? 'border-green-200' : 
                                            offer.status === 'pending' ? 'border-yellow-200' : 
                                            offer.status === 'rejected' ? 'border-red-200' : 'border-gray-200'
                                          }`}>
                                            <div className="flex items-center justify-between mb-3">
                                              <h6 className="text-sm font-medium text-gray-900">Offer #{index + 1}</h6>
                                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(offer.status)}`}>
                                                {offer.status}
                                              </span>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                              {/* Offer Details */}
                                              <div className="bg-gray-50 rounded-lg p-3">
                                                <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Offer Details</h6>
                                                <div className="space-y-1">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Offer ID:</span>
                                                    <span className="font-medium text-xs">{offer.id}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Renter ID:</span>
                                                    <span className="font-medium text-xs">{offer.renter_id}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Payment Status:</span>
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(offer.payment_status)}`}>
                                                      {offer.payment_status}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Financial Details */}
                                              <div className="bg-gray-50 rounded-lg p-3">
                                                <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Financial Details</h6>
                                                <div className="space-y-1">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Rental Price:</span>
                                                    <span className="font-medium">{formatPrice(offer.rental_price)}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Deposit:</span>
                                                    <span className="font-medium">{formatPrice(offer.deposit_amount)}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Total:</span>
                                                    <span className="font-medium font-semibold">{formatPrice(offer.total_amount)}</span>
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Rental Period */}
                                              <div className="bg-gray-50 rounded-lg p-3">
                                                <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Rental Period</h6>
                                                <div className="space-y-1">
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Start:</span>
                                                    <span className="font-medium text-xs">{formatDate(offer.start_date)}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">End:</span>
                                                    <span className="font-medium text-xs">{formatDate(offer.end_date)}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-gray-600">Duration:</span>
                                                    <span className="font-medium text-xs">{offer.rental_period} {offer.price_unit}</span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {/* Note if exists */}
                                            {offer.note && (
                                              <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                                                <span className="font-medium text-gray-600">Note: </span>
                                                <span className="text-gray-700">{offer.note}</span>
                                              </div>
                                            )}
                                          </div>
                                        ));
                                      } else {
                                        return (
                                          <div className="text-center py-4 text-gray-500">
                                            <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 01-.707-.293l-2.414-2.414a1 1 0 00-.707-.293H6.586a1 1 0 00-.707.293L3.293 13.293A1 1 0 013 13.586V13" />
                                            </svg>
                                            <p className="text-sm">No offers for this listing yet</p>
                                          </div>
                                        );
                                      }
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* User Rental Orders Tab */
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">User Rental Orders</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Rental offers this user has made for rental items
                </p>
                
                {/* Search Bar */}
                <div className="mt-4">
                  <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search rental orders by listing title, status, payment status, or user ID..."
                      value={ordersSearchTerm}
                      onChange={(e) => setOrdersSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>
              
              {filteredUserOffers.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">No rental orders found</h3>
                  <p className="mt-1 text-sm text-gray-500">This user hasn't been involved in any rental transactions yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rental Item
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rental Period
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUserOffers.map((offer) => (
                        <React.Fragment key={`offer-${offer.id}`}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{offer.listing_title}</div>
                                <div className="text-sm text-gray-500">
                                  Owner: {offer.owner_name || 'Unknown'}
                                </div>
                                <div className="text-sm text-gray-500">Rental ID: {offer.rental_listing_id}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-medium">{formatPrice(offer.total_amount)}</div>
                                <div className="text-xs text-gray-500">
                                  {formatPrice(offer.rental_price)} + {formatPrice(offer.deposit_amount)} deposit
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                Renter
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(offer.status)}`}>
                                {offer.status || 'Unknown'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="space-y-1">
                                <div className="font-medium">{offer.start_date} - {offer.end_date}</div>
                                <div className="text-xs text-gray-400">{offer.rental_period} {offer.price_unit}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="space-y-1">
                                <div className="font-medium">{formatDate(offer.created_at)}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => toggleListingExpansion(`offer-${offer.id}`)}
                                className="flex items-center justify-center cursor-pointer p-2 bg-[#e6f7f0] rounded-lg hover:bg-[#ccf0e0] transition-colors duration-200"
                              >
                                <svg 
                                  className={`w-4 h-4 text-[#00a67e] transition-transform duration-200 ${expandedListings.has(`offer-${offer.id}`) ? 'rotate-180' : ''}`} 
                                  fill="none" 
                                  viewBox="0 0 24 24" 
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                          
                          {/* Offer Details Row (expanded) */}
                          {expandedListings.has(`offer-${offer.id}`) && (
                            <tr>
                              <td colSpan={7} className="px-6 py-4 bg-[#e6f7f0]">
                                <div className="space-y-4">
                                  <h4 className="text-sm font-medium text-[#00a67e] mb-3">Rental Offer Details</h4>
                                  
                                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-600">Offer ID: </span>
                                        <span className="font-medium text-xs">{offer.id}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-600">Owner ID: </span>
                                        <span className="font-medium text-xs">{offer.rental_owner_id}</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Payment Details */}
                                  <div className="bg-white rounded-lg p-3 border border-[#00a67e]">
                                    <h5 className="text-xs font-medium text-[#00a67e] uppercase tracking-wider mb-2">Payment Details</h5>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                      {/* Amount Details */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Amount Details</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Rental Price:</span>
                                            <span className="font-medium">{formatPrice(offer.rental_price)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Deposit Amount:</span>
                                            <span className="font-medium">{formatPrice(offer.deposit_amount)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Total Amount:</span>
                                            <span className="font-medium font-semibold">{formatPrice(offer.total_amount)}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Rental Period */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Rental Period</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Start Date:</span>
                                            <span className="font-medium">{formatDate(offer.start_date)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">End Date:</span>
                                            <span className="font-medium">{formatDate(offer.end_date)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Duration:</span>
                                            <span className="font-medium">{offer.rental_period} {offer.price_unit}</span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Additional Info */}
                                      <div className="bg-gray-50 rounded-lg p-3">
                                        <h6 className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Additional Info</h6>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Created:</span>
                                            <span className="font-medium text-xs">{formatDate(offer.created_at)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Updated:</span>
                                            <span className="font-medium text-xs">{formatDate(offer.updated_at)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>


                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Rentals;
